import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    InputAdornment,
    IconButton,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    QrCodeScanner as QrIcon,
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { useAuth } from '@hooks/useAuth'
import { useTranslation } from '@i18n'
import { useUIStore } from '@store/uiStore'
import Loading from '@components/Common/Loading'

const ScannerLogin = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const { login, isLoading, loginError } = useAuth()
    const { themeSettings, setLanguage } = useUIStore()
    const [showPassword, setShowPassword] = useState(false)

    const isRTL = language === 'ar'

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
    })

    const onSubmit = async (data) => {
        try {
            const result = await login(data.email, data.password)
            // Redirect to scanner after successful login
            if (result?.user?.user_type === 'scanner') {
                navigate('/scanner')
            }
        } catch (error) {
            // Error handled in hook
        }
    }

    const handleLanguageChange = (event, newLang) => {
        if (newLang) {
            setLanguage(newLang)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1a365d 0%, #2d4a5e 50%, #1a3a6b 100%)',
                p: 2,
                position: 'relative',
            }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Language Switcher - Top Right Corner */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    left: isRTL ? 20 : 'auto',
                }}
            >
                <ToggleButtonGroup
                    value={language}
                    exclusive
                    onChange={handleLanguageChange}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        '& .MuiToggleButton-root': {
                            color: 'white',
                            border: 'none',
                            px: 2,
                            '&.Mui-selected': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.4)',
                                },
                            },
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                            },
                        },
                    }}
                >
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">عربي</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: 3,
                }}
            >
                {/* Logo/Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <QrIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {isRTL ? 'تسجيل دخول الماسح' : 'Scanner Login'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isRTL ? 'سجل دخولك للوصول إلى ماسح QR' : 'Sign in to access QR Scanner'}
                    </Typography>
                </Box>

                {/* Error Alert */}
                {loginError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {loginError.response?.data?.message || (isRTL ? 'بيانات تسجيل الدخول غير صحيحة' : 'Invalid credentials')}
                    </Alert>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        fullWidth
                        label={isRTL ? 'البريد الإلكتروني' : 'Email'}
                        type="email"
                        margin="normal"
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Invalid email format',
                            },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        InputProps={{
                            sx: { textAlign: isRTL ? 'right' : 'left' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label={isRTL ? 'كلمة المرور' : 'Password'}
                        type={showPassword ? 'text' : 'password'}
                        margin="normal"
                        {...register('password', { required: 'Password is required' })}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                            sx: { textAlign: isRTL ? 'right' : 'left' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                            mt: 3,
                            mb: 2,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1rem',
                            bgcolor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                        }}
                    >
                        {isLoading ? <Loading size={24} color="inherit" /> : (isRTL ? 'تسجيل الدخول' : 'Sign In')}
                    </Button>
                </form>
            </Paper>
        </Box>
    )
}

export default ScannerLogin
